import { useCallback, useEffect, useState } from "react";
import { toast } from "sonner";
import { useAuth } from "@/context/AuthContext";
import api, { formatApiError } from "@/lib/api";
import { can } from "@/lib/permissions";
import { PageHeader, EmptyState } from "@/components/common";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { Switch } from "@/components/ui/switch";
import {
  Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle,
} from "@/components/ui/dialog";
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from "@/components/ui/table";
import {
  ShieldCheck, Plus, PencilSimple, Trash, Eye, ArrowsClockwise,
} from "@phosphor-icons/react";

const EMPTY = {
  name: "",
  public_key: "",
  allowed_ips: "10.88.0.2/32",
  endpoint: "",
  persistent_keepalive: "25",
  enabled: true,
  notes: "",
};

function statusBadge(st) {
  const state = st?.state || "unknown";
  const variant =
    state === "online" ? "default"
      : state === "stale" ? "secondary"
        : "outline";
  const label =
    state === "online" ? "Online"
      : state === "stale" ? "Stale"
        : state === "offline" ? "Offline"
          : state;
  return <Badge variant={variant}>{label}</Badge>;
}

function fmtBytes(n) {
  if (n == null) return "—";
  const v = Number(n);
  if (!Number.isFinite(v)) return "—";
  if (v < 1024) return `${v} B`;
  if (v < 1024 ** 2) return `${(v / 1024).toFixed(1)} KiB`;
  if (v < 1024 ** 3) return `${(v / 1024 ** 2).toFixed(1)} MiB`;
  return `${(v / 1024 ** 3).toFixed(2)} GiB`;
}

export default function WireGuard() {
  const { user } = useAuth();
  const canAdd = user?.role === "admin" || can(user, "vpn-wireguard", "add");
  const canEdit = user?.role === "admin" || can(user, "vpn-wireguard", "edit");
  const canDelete = user?.role === "admin" || can(user, "vpn-wireguard", "delete");

  const [items, setItems] = useState([]);
  const [hub, setHub] = useState(null);
  const [loading, setLoading] = useState(true);
  const [open, setOpen] = useState(false);
  const [viewOpen, setViewOpen] = useState(false);
  const [edit, setEdit] = useState(null);
  const [view, setView] = useState(null);
  const [form, setForm] = useState(EMPTY);
  const [saving, setSaving] = useState(false);
  const [busyId, setBusyId] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/vpn/wireguard");
      setItems(data.items || []);
      setHub(data.hub || null);
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail, e));
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const openAdd = () => {
    setEdit(null);
    setForm({ ...EMPTY });
    setOpen(true);
  };

  const openEdit = (row) => {
    setEdit(row);
    setForm({
      name: row.name || "",
      public_key: row.public_key || "",
      allowed_ips: row.allowed_ips || "",
      endpoint: row.endpoint || "",
      persistent_keepalive: row.persistent_keepalive != null ? String(row.persistent_keepalive) : "25",
      enabled: row.enabled !== false,
      notes: row.notes || "",
    });
    setOpen(true);
  };

  const openView = async (row) => {
    try {
      const { data } = await api.get(`/vpn/wireguard/${row.id}`);
      setView(data.item || row);
      setViewOpen(true);
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail, e));
    }
  };

  const save = async () => {
    const name = (form.name || "").trim();
    const public_key = (form.public_key || "").trim();
    const allowed_ips = (form.allowed_ips || "").trim();
    const ka = parseInt(form.persistent_keepalive, 10);
    if (!name) { toast.error("Name is required"); return; }
    if (!public_key) { toast.error("Public key is required"); return; }
    if (!allowed_ips) { toast.error("Allowed IPs is required"); return; }

    setSaving(true);
    try {
      const body = {
        name,
        public_key,
        allowed_ips,
        endpoint: (form.endpoint || "").trim() || null,
        persistent_keepalive: Number.isFinite(ka) ? ka : 25,
        enabled: !!form.enabled,
        notes: form.notes || "",
        apply_live: true,
      };
      if (edit) {
        await api.patch(`/vpn/wireguard/${edit.id}`, body);
        toast.success("Peer updated and applied to hub");
      } else {
        await api.post("/vpn/wireguard", body);
        toast.success("Peer added and applied to hub");
      }
      setOpen(false);
      load();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail, e));
    } finally {
      setSaving(false);
    }
  };

  const remove = async (row) => {
    if (!window.confirm(`Delete WireGuard peer “${row.name}” and remove it from the hub?`)) return;
    setBusyId(row.id);
    try {
      await api.delete(`/vpn/wireguard/${row.id}`, { params: { apply_live: true } });
      toast.success("Peer deleted");
      load();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail, e));
    } finally {
      setBusyId(null);
    }
  };

  return (
    <div className="space-y-6" data-testid="vpn-wireguard-page">
      <PageHeader
        title="WireGuard"
        subtitle="Hub peers on this control-plane (wg-media). Add/edit/delete syncs /etc/wireguard and shows live handshake status."
      >
        <div className="flex items-center gap-2">
          <Button type="button" variant="outline" className="rounded-lg" onClick={load} data-testid="wg-refresh">
            <ArrowsClockwise className="w-4 h-4 mr-1.5" /> Refresh
          </Button>
          {canAdd && (
            <Button type="button" className="rounded-lg" onClick={openAdd} data-testid="wg-add">
              <Plus className="w-4 h-4 mr-1.5" weight="bold" /> Add peer
            </Button>
          )}
        </div>
      </PageHeader>

      {hub && (
        <div className="rounded-xl border border-border bg-card px-4 py-3 text-sm flex flex-wrap gap-x-6 gap-y-1">
          <span><span className="text-muted-foreground">Interface</span>{" "}
            <code className="font-mono">{hub.interface || "wg-media"}</code></span>
          <span><span className="text-muted-foreground">Hub public key</span>{" "}
            <code className="font-mono text-xs break-all">{hub.public_key || "—"}</code></span>
          <span><span className="text-muted-foreground">Listen</span>{" "}
            <span className="tabular-nums">{hub.listen_port ?? "—"}</span></span>
          {hub.error && <span className="text-destructive">{hub.error}</span>}
        </div>
      )}

      {loading ? (
        <p className="text-sm text-muted-foreground">Loading…</p>
      ) : items.length === 0 ? (
        <EmptyState
          testid="wg-empty"
          icon={ShieldCheck}
          title="No WireGuard peers"
          description="Add a peer (media FreeSWITCH, etc.) with its public key and AllowedIPs."
        />
      ) : (
        <div className="rounded-xl border border-border bg-card card-shadow overflow-hidden">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Name</TableHead>
                <TableHead>Allowed IPs</TableHead>
                <TableHead>Endpoint</TableHead>
                <TableHead>Status</TableHead>
                <TableHead>Handshake</TableHead>
                <TableHead className="text-right">Actions</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {items.map((row) => (
                <TableRow key={row.id} data-testid={`wg-row-${row.id}`}>
                  <TableCell className="font-medium">
                    {row.name}
                    {!row.enabled && (
                      <Badge variant="outline" className="ml-2">Disabled</Badge>
                    )}
                  </TableCell>
                  <TableCell className="font-mono text-xs">{row.allowed_ips}</TableCell>
                  <TableCell className="font-mono text-xs">
                    {row.status?.live_endpoint || row.endpoint || "—"}
                  </TableCell>
                  <TableCell>{statusBadge(row.status)}</TableCell>
                  <TableCell className="text-sm text-muted-foreground">
                    {row.status?.latest_handshake_ago || "never"}
                  </TableCell>
                  <TableCell className="text-right">
                    <div className="inline-flex items-center gap-1">
                      <Button type="button" size="sm" variant="ghost" onClick={() => openView(row)} title="View">
                        <Eye className="w-4 h-4" />
                      </Button>
                      {canEdit && (
                        <Button type="button" size="sm" variant="ghost" onClick={() => openEdit(row)} title="Edit">
                          <PencilSimple className="w-4 h-4" />
                        </Button>
                      )}
                      {canDelete && (
                        <Button
                          type="button" size="sm" variant="ghost"
                          disabled={busyId === row.id}
                          onClick={() => remove(row)} title="Delete"
                        >
                          <Trash className="w-4 h-4 text-destructive" />
                        </Button>
                      )}
                    </div>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </div>
      )}

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="sm:max-w-lg">
          <DialogHeader>
            <DialogTitle>{edit ? "Edit WireGuard peer" : "Add WireGuard peer"}</DialogTitle>
          </DialogHeader>
          <div className="space-y-3 py-1">
            <div className="space-y-1.5">
              <Label>Name</Label>
              <Input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} placeholder="media-.43" />
            </div>
            <div className="space-y-1.5">
              <Label>Public key</Label>
              <Input
                className="font-mono text-xs"
                value={form.public_key}
                onChange={(e) => setForm({ ...form, public_key: e.target.value })}
                placeholder="base64 public key"
              />
            </div>
            <div className="space-y-1.5">
              <Label>Allowed IPs</Label>
              <Input
                className="font-mono text-xs"
                value={form.allowed_ips}
                onChange={(e) => setForm({ ...form, allowed_ips: e.target.value })}
                placeholder="10.88.0.2/32"
              />
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div className="space-y-1.5">
                <Label>Endpoint (optional)</Label>
                <Input
                  className="font-mono text-xs"
                  value={form.endpoint}
                  onChange={(e) => setForm({ ...form, endpoint: e.target.value })}
                  placeholder="157.90.96.43:51820"
                />
              </div>
              <div className="space-y-1.5">
                <Label>Keepalive (sec)</Label>
                <Input
                  value={form.persistent_keepalive}
                  onChange={(e) => setForm({ ...form, persistent_keepalive: e.target.value })}
                />
              </div>
            </div>
            <div className="space-y-1.5">
              <Label>Notes</Label>
              <Input value={form.notes} onChange={(e) => setForm({ ...form, notes: e.target.value })} />
            </div>
            <div className="flex items-center gap-2 pt-1">
              <Switch checked={!!form.enabled} onCheckedChange={(v) => setForm({ ...form, enabled: v })} />
              <Label>Enabled (applied on hub)</Label>
            </div>
          </div>
          <DialogFooter>
            <Button type="button" variant="outline" onClick={() => setOpen(false)}>Cancel</Button>
            <Button type="button" onClick={save} disabled={saving}>
              {saving ? "Saving…" : edit ? "Save" : "Add peer"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog open={viewOpen} onOpenChange={setViewOpen}>
        <DialogContent className="sm:max-w-lg">
          <DialogHeader>
            <DialogTitle>Peer — {view?.name}</DialogTitle>
          </DialogHeader>
          {view && (
            <div className="space-y-2 text-sm">
              <div className="flex justify-between gap-4"><span className="text-muted-foreground">Status</span>{statusBadge(view.status)}</div>
              <div className="flex justify-between gap-4"><span className="text-muted-foreground">Handshake</span><span>{view.status?.latest_handshake_ago || "never"}</span></div>
              <div className="flex justify-between gap-4"><span className="text-muted-foreground">Live endpoint</span><code className="font-mono text-xs">{view.status?.live_endpoint || "—"}</code></div>
              <div className="flex justify-between gap-4"><span className="text-muted-foreground">Transfer RX</span><span>{fmtBytes(view.status?.transfer_rx_bytes)}</span></div>
              <div className="flex justify-between gap-4"><span className="text-muted-foreground">Transfer TX</span><span>{fmtBytes(view.status?.transfer_tx_bytes)}</span></div>
              <div className="flex justify-between gap-4"><span className="text-muted-foreground">In kernel</span><span>{view.status?.in_kernel ? "yes" : "no"}</span></div>
              <hr className="border-border" />
              <div><span className="text-muted-foreground">Public key</span><pre className="mt-1 font-mono text-xs break-all whitespace-pre-wrap">{view.public_key}</pre></div>
              <div className="flex justify-between gap-4"><span className="text-muted-foreground">Allowed IPs</span><code className="font-mono text-xs">{view.allowed_ips}</code></div>
              <div className="flex justify-between gap-4"><span className="text-muted-foreground">Configured endpoint</span><code className="font-mono text-xs">{view.endpoint || "—"}</code></div>
              <div className="flex justify-between gap-4"><span className="text-muted-foreground">Keepalive</span><span>{view.persistent_keepalive ?? "—"}</span></div>
              {view.notes ? <div><span className="text-muted-foreground">Notes</span><p className="mt-1">{view.notes}</p></div> : null}
            </div>
          )}
          <DialogFooter>
            <Button type="button" variant="outline" onClick={() => setViewOpen(false)}>Close</Button>
            {canEdit && view && (
              <Button type="button" onClick={() => { setViewOpen(false); openEdit(view); }}>Edit</Button>
            )}
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
